import { useRef, useState, type DragEvent, type ReactNode } from 'react';
import type { Asset } from '@studio/shared';

import { api, assetUrl } from '../lib/api';
import { downscaleImage } from '../lib/downscale';
import { useErrorText } from '../lib/errors';
import { useT } from '../lib/i18n';
import { PlusIcon, TrashIcon } from './Icons';

const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp'];

interface Props {
  /** What the image is filed as. */
  kind: 'reference' | 'backdrop';
  assetId?: string;
  onChange: (asset: Asset | undefined) => void;
  /** Shown in the empty drop zone. */
  prompt: ReactNode;
  /** Aspect of the preview once there is an image. */
  ratio?: string;
}

/**
 * One image, from wherever it is: dropped, picked, or pasted as an address.
 *
 * The three are one control because they are one decision — "this picture" —
 * and a photograph seen on someone's feed is as often an address on the
 * clipboard as a file on the desk. A page address works as well as an image
 * address: the server takes the picture the page says it shows.
 */
export function ImageSource({ kind, assetId, onChange, prompt, ratio = '4 / 5' }: Props) {
  const t = useT();
  const describe = useErrorText();
  const fileRef = useRef<HTMLInputElement>(null);
  const depth = useRef(0);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');

  const take = async (work: () => Promise<Asset>) => {
    setBusy(true);
    setError('');
    try {
      onChange(await work());
      setUrl('');
    } catch (err) {
      setError(describe(err, 'source.failed'));
    } finally {
      setBusy(false);
    }
  };

  const fromFile = (file: File | undefined) => {
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) {
      setError(t('source.notAnImage'));
      return;
    }
    void take(async () => api.uploadAsset(await downscaleImage(file), kind));
  };

  const drag = (event: DragEvent<HTMLDivElement>, change: -1 | 0 | 1) => {
    event.preventDefault();
    if (change !== 0) {
      depth.current = Math.max(0, depth.current + change);
      setOver(depth.current > 0);
    }
  };

  if (assetId) {
    return (
      <div className="source source--filled" style={{ aspectRatio: ratio }}>
        <img src={assetUrl(assetId)} alt="" />
        <button
          type="button"
          className="source__clear"
          aria-label={t('source.remove')}
          onClick={() => onChange(undefined)}
        >
          <TrashIcon />
        </button>
      </div>
    );
  }

  return (
    <div className="stack stack--sm">
      <div
        className={`source ${over ? 'is-over' : ''}`}
        onDragEnter={(event) => drag(event, 1)}
        onDragOver={(event) => drag(event, 0)}
        onDragLeave={(event) => drag(event, -1)}
        onDrop={(event) => {
          drag(event, 0);
          depth.current = 0;
          setOver(false);
          fromFile(event.dataTransfer.files?.[0]);
        }}
      >
        <button
          type="button"
          className="source__pick"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
        >
          {busy ? <span className="spinner" /> : <PlusIcon />}
          <span className="source__prompt">{busy ? t('source.working') : prompt}</span>
          <span className="faint">{t('source.dropOrClick')}</span>
        </button>
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPTED.join(',')}
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
          onChange={(event) => {
            fromFile(event.target.files?.[0]);
            event.target.value = '';
          }}
        />
      </div>
      <form
        className="row"
        onSubmit={(event) => {
          event.preventDefault();
          if (url.trim()) void take(() => api.importAsset(url.trim(), kind));
        }}
      >
        <input
          className="input grow"
          type="url"
          inputMode="url"
          value={url}
          placeholder={t('source.urlPlaceholder')}
          aria-label={t('source.urlLabel')}
          onChange={(event) => setUrl(event.target.value)}
        />
        <button type="submit" className="btn" disabled={busy || !url.trim()}>
          {t('source.import')}
        </button>
      </form>
      {error && <div className="banner banner--error">{error}</div>}
    </div>
  );
}
